import { useState } from "react";
import { X, Loader2, Eye, EyeOff, MailPlus } from "lucide-react";
import { toast } from "sonner";

export const AddAccountModal = ({ api, onClose, onAdded }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/mail/accounts/add", { email: email.trim().toLowerCase(), password });
      toast.success(`Added ${data.active} — switched to it`);
      onAdded?.();
      onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : "Could not add that mailbox — check the password";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const input =
    "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";

  return (
    <div data-lenis-prevent className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-6" data-testid="add-account-modal" onClick={onClose}>
      <div className="w-full max-w-md bg-nx-navy2 border border-white/10 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="font-marcellus text-lg text-white flex items-center gap-2.5"><MailPlus size={17} className="text-nx-goldlight" /> Add a mailbox</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors p-1" aria-label="Close" data-testid="add-account-close">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-4 p-6" autoComplete="off">
          <p className="text-xs text-slate-500 leading-relaxed">
            Add another @nexsecure.in mailbox to this sign-in — exactly like adding an account in Outlook. Its password is verified against the mail server once and stored encrypted; you can switch between mailboxes from the sidebar anytime.
          </p>
          <input
            type="email" required autoComplete="off" data-lpignore="true"
            placeholder="mailbox@nexsecure.in"
            className={input}
            value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="add-account-email"
          />
          <div className="relative">
            <input
              type={showPass ? "text" : "password"} required autoComplete="new-password" data-lpignore="true"
              placeholder="That mailbox's password"
              className={`${input} pr-12`}
              value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="add-account-password"
            />
            <button
              type="button" onClick={() => setShowPass(!showPass)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
              aria-label={showPass ? "Hide password" : "Show password"}
              data-testid="add-account-show-password"
            >
              {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {error && (
            <p className="text-xs text-red-400 border border-red-500/30 bg-red-500/[0.06] px-3 py-2.5" data-testid="add-account-error" role="alert">
              {error}
            </p>
          )}
          <button
            type="submit" disabled={busy}
            className="flex items-center justify-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold tracking-wide text-sm px-8 py-3.5 transition-colors disabled:opacity-60"
            data-testid="add-account-submit"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <MailPlus size={15} />}
            {busy ? "Verifying…" : "Add mailbox"}
          </button>
        </form>
      </div>
    </div>
  );
};
